import LegalLayout, { LegalSection, legalLink } from "@/components/legal/LegalLayout";
import useSeo from "@/hooks/useSeo";

const MEASURES = [
  "Semantic HTML with one main landmark per page, a skip-to-content link, and a logical heading order",
  "Every interactive control is reachable and operable by keyboard, with a visible focus indicator",
  "Text and interface contrast targeted at WCAG 2.2 AA minimums on both dark and light sections",
  "Descriptive alternative text on meaningful images; decorative graphics are hidden from assistive technology",
  "Form fields with programmatic labels, inline error messages, and no time limits",
  "Motion and auto-playing transitions are reduced when your system has 'reduce motion' turned on",
  "Text can be resized to 200% and the layout reflows on screens down to 320px wide without horizontal scrolling",
];

const LIMITATIONS = [
  ["Stripe checkout", "Payment pages are hosted by Stripe. We chose Stripe in part for its accessibility work, but we don't control that interface."],
  ["Live client site previews", "The 'Live preview' modal embeds another website. Those sites are built to the same standards, but the embedded frame itself may be harder to navigate with a screen reader — the 'Open site' button opens the same site directly."],
  ["Client-supplied media", "Photos and short clips in the Local Visibility samples come from clients and may not have detailed descriptions beyond the caption we provide."],
];

export default function Accessibility() {
  useSeo({
    title: "Accessibility Statement — WebJesters",
    description:
      "WebJesters' commitment to an accessible website: WCAG 2.2 AA target, measures we take, known limitations, and how to request help or report a barrier.",
    path: "/accessibility",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "WebJesters", item: "https://webjesters.com/" },
        { "@type": "ListItem", position: 2, name: "Accessibility Statement", item: "https://webjesters.com/accessibility" },
      ],
    },
  });

  return (
    <LegalLayout title="Accessibility Statement" eyebrow="Legal" lastUpdated="October 16, 2026">
      <p>
        WebJesters LLC wants everyone to be able to use webjesters.com — including people who rely on screen readers,
        keyboard navigation, magnification, voice control, or reduced-motion settings. This statement describes the
        standard we work to, what we've done, where we know there are gaps, and how to reach us if something gets in
        your way.
      </p>

      <LegalSection title="Our standard">
        <p>
          We aim to conform to the <strong className="text-white">Web Content Accessibility Guidelines (WCAG) 2.2,
          Level AA</strong>, published by the W3C. We treat this as an ongoing commitment rather than a one-time
          project, and we review the site whenever we change its design or add features.
        </p>
      </LegalSection>

      <LegalSection title="What we've done">
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          {MEASURES.map((m) => <li key={m}>{m}</li>)}
        </ul>
      </LegalSection>

      <LegalSection title="Known limitations">
        <p>We're upfront about the parts we don't fully control or haven't finished:</p>
        <ul className="space-y-3">
          {LIMITATIONS.map(([h, p]) => (
            <li key={h}>
              <strong className="text-white">{h}.</strong> {p}
            </li>
          ))}
        </ul>
      </LegalSection>

      <LegalSection title="Tell us if something isn't working" id="feedback">
        <p data-testid="accessibility-contact">
          If you hit a barrier on this site, or need any information in a different format, email{" "}
          <a href="mailto:team@webjesters.com?subject=Accessibility" className={legalLink}>team@webjesters.com</a>{" "}
          or call <a href="tel:6023905623" className={legalLink}>602-390-5623</a> (Mon–Fri, 9am–5pm Arizona). Tell us
          the page, what you were trying to do, and the browser or assistive technology you were using. We reply within
          one business day and aim to fix confirmed issues within ten business days.
        </p>
      </LegalSection>

      <LegalSection title="Hosted client websites">
        <p>
          Websites we build for clients are built with the same practices listed above. Each client controls their own
          content, images, and any third-party tools they ask us to add, so accessibility questions about a client's
          site should go to that business first; we're glad to help them address anything raised.
        </p>
      </LegalSection>

      <LegalSection title="Technical details">
        <p>
          webjesters.com is built with HTML, CSS, and JavaScript (React). It relies on these technologies working in
          your browser along with any assistive technology you use. It was last reviewed on the date above using
          keyboard-only navigation, browser accessibility tooling, and screen-reader spot checks on desktop and mobile.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
